'use client'

import { Image } from 'cms-types'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import modalService from '../../service/modalService'
import AccountNav, { type AccountNavItem } from './AccountNav'
import BurgerMenu from './BurgerMenu'
import SearchBar from './SearchBar'
import TopNavBar from './TopNavBar'

type NavigationProps = {
  onShoppingBasketIconClick: () => void
  shoppingBasketItemsCount?: number
  onSearchSubmit: (query: string) => void
  logo?: Image
  logoUrl: string
  menuItems?: {
    id?: string | null
    url: string
    title: string
    isActive?: boolean
  }[]
  burgerMenuAdditionalListItems?: {
    id?: string | null
    url: string
    title: string
  }[]
  searchEnabled?: boolean | null
  searchFieldPlaceholder?: string | null
  searchButtonLabel?: string
  closeSearchButtonLabel?: string
  searchSuggestion?: {
    title: string
    links: {
      title: string
      id?: string | null
    }[]
  }
  shoppingBasketEnabled?: boolean | null
  loginEnabled?: boolean | null
  loginTypography?: {
    login: string
    profile: string
    logout: string
  }
  isLogin: boolean
  /**
   * First name of the logged-in customer, used to indicate the logged-in state
   */
  firstName?: string | null
  /**
   * Specific variable for Storybook preview
   */
  isPreview?: boolean
  goToLogout: () => void
  goToLogin: () => void
  goToProfile: () => void
  goToDefaultAccountPage: () => void
  accountNavTitle?: string
  accountNavItems?: AccountNavItem[]
  accountNavCloseButtonLabel?: string
}

export default function Navigation({
  onShoppingBasketIconClick,
  shoppingBasketItemsCount,
  onSearchSubmit,
  logo,
  logoUrl,
  menuItems,
  burgerMenuAdditionalListItems,
  searchEnabled,
  searchFieldPlaceholder,
  searchButtonLabel,
  closeSearchButtonLabel,
  shoppingBasketEnabled,
  loginEnabled,
  loginTypography,
  isLogin,
  firstName,
  isPreview,
  searchSuggestion,
  goToLogin,
  goToLogout,
  goToProfile,
  goToDefaultAccountPage,
  accountNavTitle,
  accountNavItems,
  accountNavCloseButtonLabel,
}: NavigationProps) {
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false)
  const [isSearchBarCloseAnimationStarted, setIsSearchBarOpenAnimationStarted] = useState(false)

  const [isBurgerMenuOpen, setBurgerMenuOpen] = useState(false)
  const [isBurgerMenuCloseAnimationStarted, setIsBurgerMenuCloseAnimationStarted] = useState(false)

  const [isAccountNavOpen, setIsAccountNavOpen] = useState(false)
  const pathname = usePathname()
  const isAccountPage =
    (pathname?.startsWith('/account') || pathname?.startsWith('/shop/wunschset')) ?? false

  const onOpenBurgerMenu = useCallback(() => {
    modalService.changeModalState('burgerMenu', true)
    setBurgerMenuOpen(true)
    setIsBurgerMenuCloseAnimationStarted(false)
    document.body.classList.add('cancelScroll')
  }, [])

  const onCloseBurgerMenu = useCallback(() => {
    setIsBurgerMenuCloseAnimationStarted(true)
  }, [])

  const onCloseBurgerMenuAnimationEnd = useCallback(() => {
    if (isBurgerMenuCloseAnimationStarted) {
      setBurgerMenuOpen(false)
      modalService.changeModalState('burgerMenu', false)
      setIsBurgerMenuCloseAnimationStarted(false)
    }
  }, [isBurgerMenuCloseAnimationStarted])

  const onOpenSearchBar = useCallback(() => {
    modalService.changeModalState('searchBar', true)
    setIsSearchBarOpen(true)
    setIsSearchBarOpenAnimationStarted(false)

    if (!document.body.classList.contains('cancelScroll')) {
      document.body.classList.add('cancelScroll')
    }
  }, [])

  const onCloseSearchBar = useCallback(() => {
    setIsSearchBarOpenAnimationStarted(true)
  }, [])

  const onSearchBarCloseAnimationEnd = useCallback(() => {
    if (isSearchBarCloseAnimationStarted) {
      setIsSearchBarOpenAnimationStarted(false)
      setIsSearchBarOpen(false)
      modalService.changeModalState('searchBar', false)
    }
  }, [isSearchBarCloseAnimationStarted])

  useEffect(() => {
    modalService.addActionOnScreenChange('burgerMenu', {
      onCloseDesktop: () => {
        setBurgerMenuOpen(false)
      },
    })
  }, [])

  useEffect(() => {
    if (!searchEnabled) return

    const searchOpenWithKeyboard = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.ctrlKey && keyboardEvent.key === 'k') {
        keyboardEvent.preventDefault()

        onOpenSearchBar()
      }
    }

    window.addEventListener('keydown', searchOpenWithKeyboard)

    return () => {
      window.removeEventListener('keydown', searchOpenWithKeyboard)
    }
  }, [onOpenSearchBar, searchEnabled])

  const onAccountIconClick = useCallback(() => {
    if (!isLogin) {
      goToLogin()
      return
    }

    if (accountNavItems != null && accountNavItems.length > 0) {
      setIsAccountNavOpen(true)
      return
    }

    goToProfile()
  }, [accountNavItems, goToLogin, goToProfile, isLogin])

  const onAccountNavClose = useCallback(() => {
    setIsAccountNavOpen(false)
  }, [])

  return (
    <nav className='relative w-full'>
      <TopNavBar
        isBurgerMenuOpen={isBurgerMenuOpen && !isBurgerMenuCloseAnimationStarted}
        onOpenBurgerMenu={onOpenBurgerMenu}
        shoppingBasketItemsCount={shoppingBasketItemsCount}
        onCloseBurgerMenu={onCloseBurgerMenu}
        searchEnabled={searchEnabled}
        onOpenSearch={onOpenSearchBar}
        onCloseSearch={onCloseSearchBar}
        isSearchBarOpen={isSearchBarOpen}
        searchButtonLabel={searchButtonLabel}
        shoppingBasketEnabled={shoppingBasketEnabled}
        onShoppingIconClick={onShoppingBasketIconClick}
        menuItems={menuItems}
        logo={logo}
        logoUrl={logoUrl}
        isPreview={isPreview}
        loginEnabled={loginEnabled}
        isLogin={isLogin}
        firstName={firstName}
        goToLogin={goToLogin}
        goToProfile={goToProfile}
        onAccountIconClick={onAccountIconClick}
        isAccountPage={isAccountPage}
      />

      {isSearchBarOpen && (
        <SearchBar
          isOpen={isSearchBarOpen}
          isCloseAnimationStarted={isSearchBarCloseAnimationStarted}
          onClose={onCloseSearchBar}
          onCloseAnimationEnd={onSearchBarCloseAnimationEnd}
          onSubmit={onSearchSubmit}
          placeholder={searchFieldPlaceholder}
          closeButtonLabel={closeSearchButtonLabel}
          searchButtonLabel={searchButtonLabel}
          searchSuggestion={searchSuggestion}
        />
      )}

      {isBurgerMenuOpen && (
        <BurgerMenu
          isOpen={isBurgerMenuOpen}
          isCloseAnimationStarted={isBurgerMenuCloseAnimationStarted}
          onCloseAnimationEnd={onCloseBurgerMenuAnimationEnd}
          menuItems={menuItems}
          additionalListItems={burgerMenuAdditionalListItems}
          loginEnabled={Boolean(loginEnabled)}
          loginTypography={loginTypography}
          isLogin={isLogin}
          firstName={firstName}
          onNavigation={onCloseBurgerMenu}
          goToLogin={goToLogin}
          goToLogout={goToLogout}
          goToDefaultAccountPage={goToDefaultAccountPage}
        />
      )}

      {isAccountNavOpen && accountNavItems != null && accountNavItems.length > 0 && (
        <AccountNav
          isOpen={isAccountNavOpen}
          title={accountNavTitle ?? 'Account'}
          items={accountNavItems}
          onClose={onAccountNavClose}
          closeButtonLabel={accountNavCloseButtonLabel}
        />
      )}
    </nav>
  )
}
